import {
  createListenerMiddleware,
  createSelector,
  createSlice,
  PayloadAction,
  TypedStartListening,
} from "@reduxjs/toolkit"
import { FormikErrors, FormikValues } from "formik"
import * as Yup from "yup"
import { RootState } from "@/store"
import { mockDataEnabled, mockSessionEnabled } from "@/store/apis/api.slice"
import { PERSIST_FORMS_SESSION_KEY } from "@/utils/helpers/mappings"
import { setMemberId } from "@/store/member/member.slice"
import { FactorTypes } from "@/types/Okta"

export const SUBMISSION_STATE_SUCCESS = "success"
export const SUBMISSION_STATE_ERROR = "error"

interface FormState {
  /**
   * A completed form submission
   */
  submission: Submission | null
  /**
   * Used for MFA states
   */
  pendingSubmission: FormikValues | null
  factorSelect: boolean
  factorSent: FactorTypes | null
  resumeNotice: boolean
  /**
   * If there was an issue with the resumed session data
   */
  resumeFormsError: boolean
  /**
   * Forms that have been initiated/mounted by
   * the user within the session
   */
  persistForms: (PersistForm | MultiStepPersistForm)[]
  /**
   * Store multistep values / errors so
   * other elements can react
   */
  multiStepForms: StoredMultiStepForm[]
}

type StoredMultiStepForm = MultiStepForm & {
  values: {
    values: FormikValues
    keyOrName: string
  }[]
  errors: {
    errors: FormikErrors<FormikValues>
    keyOrName: string
  }[]
}

export interface FormInterface {
  name: string
}

export interface PersistForm extends FormInterface {
  values: FormikValues
  version: number
  /**
   * Epoch timestamp
   */
  ts: number
}

export interface MultiStepForm extends FormInterface {
  currentStep: number
}

export interface FormStep<Key extends string = string> {
  values: FormikValues
  /**
   *  Unique key each step must have (within the same form)
   *  which is used to retain and restore data. This allows
   *  other attributes such as name to change without breaking
   */
  key: Key
}

export interface MultiStepPersistForm
  extends MultiStepForm,
    Omit<PersistForm, "values"> {
  values: FormStep[]
}

function isMultiStepPersist(
  values: FormikValues | FormStep
): values is FormStep {
  return Object.keys(values).every((key) => ["key", "values"].includes(key))
}

type MultiStepFormStepPayload = MultiStepForm &
  FormStep & { errors?: FormikErrors<FormikValues> }

type MultiStepFormPayload = Omit<
  StoredMultiStepForm,
  "errors" | "currentStep"
> &
  Partial<StoredMultiStepForm>

interface Submission {
  state: typeof SUBMISSION_STATE_ERROR | typeof SUBMISSION_STATE_SUCCESS
  name: string
  values?: FormikValues

  [index: string]: any
}

/**
 * Schema used to validate the stored structure is compatible
 */
export const persistFormSchema = Yup.array().of(
  Yup.object({
    name: Yup.string().required(),
    version: Yup.number().required(),
    currentStep: Yup.number(),
    values: Yup.mixed()
      .required()
      .when("currentStep", {
        is: (value: any) => value !== undefined,
        then: () =>
          Yup.array()
            .required()
            .of(
              Yup.object({
                key: Yup.string().required(),
                values: Yup.object().required(),
              })
            ),
        otherwise: () => Yup.object(),
      }),
  })
)

function restoreFormsSession(): Pick<FormState, "persistForms"> &
  Partial<Pick<FormState, "resumeFormsError">> {
  try {
    const resumed = JSON.parse(
      window.sessionStorage.getItem(PERSIST_FORMS_SESSION_KEY) ?? "[]"
    ) as FormState["persistForms"]
    persistFormSchema.validateSync(resumed)
    return { persistForms: resumed, resumeFormsError: false }
  } catch (e) {
    console.warn(e)
    return { persistForms: [], resumeFormsError: true }
  }
}

const initialState: FormState = {
  submission: null,
  pendingSubmission: null,
  factorSelect: false,
  factorSent: null,
  resumeNotice: true,
  resumeFormsError: false,
  multiStepForms: [],
  persistForms: [],
}

let sessionState = mockDataEnabled ? {} : restoreFormsSession()

// inject mocking apis for development
if (process.env.NODE_ENV === "development" && mockSessionEnabled) {
  // using require avoid webpack including the mockjs
  sessionState = require("./mockForm")["mockFormSession"]
}

export const formSlice = createSlice({
  name: "form",
  initialState: {
    ...initialState,
    ...sessionState,
  },
  reducers: {
    setSubmission: (state, { payload }: PayloadAction<Submission>) => {
      window.dataLayer?.push({
        event: "Form Submission",
        name: payload.name,
        state: payload.state,
      })
      state.submission = payload
    },
    setPendingSubmission: (state, { payload }) => {
      state.pendingSubmission = payload
    },
    setFactorSelect: (state, { payload }: PayloadAction<boolean>) => {
      state.factorSelect = payload
    },
    setFactorSent: (state, { payload }: PayloadAction<FactorTypes>) => {
      state.factorSent = payload
    },
    setResumeNotice: (state, { payload }: PayloadAction<boolean>) => {
      state.resumeNotice = payload
    },
    resetFactors: (state, { payload }: PayloadAction<undefined | true>) => {
      const { pendingSubmission, factorSelect, factorSent } = initialState
      // reset all mfa back to initial state
      if (payload) {
        return { ...state, factorSent, factorSelect, pendingSubmission }
      }

      // reset just the select/sent states
      return { ...state, factorSelect, factorSent }
    },
    resetSubmission: (state) => {
      Object.assign(state, {
        submission: null,
        pendingSubmission: null,
        factorSelect: null,
        factorSent: null,
      })
    },
    setMultiStepFormStep: (
      state,
      { payload }: PayloadAction<MultiStepFormStepPayload>
    ) => {
      const { name, key, values, errors, currentStep } = payload

      const existing = state.multiStepForms.find((item) => item.name === name)

      const keyOrName = key ?? name

      if (existing) {
        existing.values = existing.values.filter(
          (item) => item.keyOrName !== keyOrName
        )
        existing.errors = existing.errors.filter(
          (item) => item.keyOrName !== keyOrName
        )

        existing.values.push({ keyOrName, values })

        if (errors) {
          existing.errors.push({ keyOrName, errors })
        }

        existing.currentStep = currentStep
      } else {
        state.multiStepForms.push({
          name,
          values: [{ keyOrName, values }],
          errors: errors ? [{ keyOrName, errors }] : [],
          currentStep,
        })
      }
    },
    setMultiStepForm: (
      state,
      { payload }: PayloadAction<MultiStepFormPayload>
    ) => {
      const { name, values, errors, currentStep } = payload
      const existing = state.multiStepForms.find((item) => item.name === name)
      if (existing) {
        existing.values = values
        existing.errors = errors ?? []
        existing.currentStep = currentStep ?? 0
      } else {
        state.multiStepForms.push({
          name,
          values,
          errors: errors ?? [],
          currentStep: currentStep ?? 0,
        })
      }
    },
    setPersistForm: (
      state,
      {
        payload,
      }: PayloadAction<
        Omit<PersistForm | MultiStepPersistForm, "ts"> & Partial<MultiStepForm>
      >
    ): void => {
      // find the persistent state for the relevant form
      const existing = state.persistForms.find(
        (item) => item.name === payload.name
      )

      const { values } = payload
      const ts = new Date().getTime()

      if (!existing) {
        state.persistForms.push({
          ...payload,
          values: isMultiStepPersist(values) ? [values] : values,
          ts,
        })
      } else {
        if (isMultiStepPersist(values)) {
          const steps = Array.isArray(existing.values)
            ? existing.values.filter((step) => step.key !== values.key)
            : []
          Object.assign(existing, payload, {
            values: [...steps, payload.values],
            ts,
          })
        } else {
          Object.assign(existing, payload, { ts })
        }
      }
    },
    resetInitialState: (state) =>
      Object.assign(state, {
        ...initialState,
      }),
  },
})

export const {
  setSubmission,
  setPendingSubmission,
  setFactorSelect,
  setFactorSent,
  setResumeNotice,
  resetFactors,
  resetSubmission,
  setPersistForm,
  setMultiStepFormStep,
  resetInitialState,
} = formSlice.actions

export const writeFormSessionMiddleware = createListenerMiddleware()

const startAppListening =
  writeFormSessionMiddleware.startListening as TypedStartListening<RootState>

if (!mockSessionEnabled) {
  startAppListening({
    actionCreator: setPersistForm,
    effect: (_action, { getState }) => {
      const { form } = getState() as RootState
      window.sessionStorage.setItem(
        PERSIST_FORMS_SESSION_KEY,
        JSON.stringify(form.persistForms)
      )
    },
  })

  startAppListening({
    predicate: (action, currentState, prevState) => {
      return setMemberId.match(action) && prevState.member.memberId
    },
    effect: (_action, { dispatch }) => {
      dispatch(resetInitialState())
    },
  })
}

const selectForm = (state: RootState) => state.form

export const selectSubmission = (state: RootState) => state.form.submission
export const selectPendingSubmission = (state: RootState) =>
  state.form.pendingSubmission
export const selectFactorSelect = (state: RootState) => state.form.factorSelect
export const selectFactorSent = (state: RootState) => state.form.factorSent
export const selectResumeNotice = (state: RootState) => state.form.resumeNotice

export const selectPersistForm = createSelector(
  [selectForm, (_state, name: string) => name],
  (state, name) => {
    return state.persistForms.find((value) => value.name === name)
  }
)

export const selectMultiStepForm = createSelector(
  [selectForm, (_state, name: string) => name],
  (state, name) => state.multiStepForms.find((form) => form.name === name)
)

export default formSlice.reducer
